/** @module Auth0YofiTelemetryHelper */

/* eslint-disable import/extensions */
import Auth0CookieHelper from './auth0-cookie-helper.mjs';

const yofiPackageUrl = 'https://static-resources.yofi.ai/sdk/yofi-telemetry.js';

const yofiAccessTokenCookieName = 'yofi-at';


class Auth0YofiTelemetryHelper {
  /**
   * @returns {import('./auth0-dynamic-script-loader.mjs').Script}
   */
  static getInitializerScript = () => ({
    url: yofiPackageUrl,
    attributes: { defer: 'defer' },
    preLoad: this.registerYofiConfigForPackageToInit,
    required: false,
  });


  /**
   * Attach the config values to the window object in the manner Yofi expects us to {@link https://docs.yofi.ai/yofi-telemetry/telemetry-web-sdk/quick-start/quick-start-on-wordpress}
   */
  static registerYofiConfigForPackageToInit = () => {
    console.log('Auth0YofiTelemetryHelper._registerYofiConfigForPackageToInit');
    try {
      const publicToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhcHBfaWQiOiJlYmJlOWYyZC02MWJkLTQ5YmMtYjZhZi03NTIwNDcxMmQ4MTEiLCJqdGkiOiI1MjhiZDMxOS1jZjRlLTQ2ZTQtYmVjZC04MjFkMmY4OGRjOTYiLCJ0eXBlIjoxfQ.VZLyQJVvMKIKSdvLgtf6XrkdCmPPQeAaV64jVlcWyCk';
      const journeyIdSalt = 'login.shipstation.com';

      window.__yofi_telemetry_data = {
        public_token: publicToken,
        journey_id_salt: journeyIdSalt,
      };

      window.YofiSessionInstance = { ...this._registerMethodsForFutureYofiExtension() };
    } catch (e) {
      console.error('ruh roh', e);
    }
  };


  /**
   * For any interactions with the Yofi Session object we'll need to declare those handlers here.
   * @returns {Record<string, callback>}
   */
  static _registerMethodsForFutureYofiExtension = () => ({
    /**
     * Tag the user sessions with SS-determined metadata
     * @param {Record<string, string>} labels
     */
    addLabels: (labels) => {
      window.__yofi_telemetry_data.labels = {
        ...labels,
      };
    },
  });


  /**
   * Set the yofi access token as a cookie so it can be picked up by post-login applications
   * @returns {void}
   */
  static setCookieWithAccessToken = async () => {
    console.log('Auth0YofiTelemetryHelper.setCookieWithAccessToken');
    try {
      const accessToken = await this._getAccessToken();
      Auth0CookieHelper.setCookie(
        yofiAccessTokenCookieName,
        accessToken,
        {
          expiry: Auth0CookieHelper.getExpirationFromDays(1),
          domain: window.location.home,
        },
      );
    } catch (e) {
      console.log('error setting Yofi cookie', e);
    }
  };

  /**
   * Makes best effort to set the yofi access token as a cookie so it can be picked up by post-login applications
   * and gives up after timeoutMs milliseconds (this avoids hanging the login flow if Yofi is being slow or is down)
   * @returns {void}
   */
  static setCookieWithAccessTokenSafe = async (timeoutMs = 3000) => {
    try {
      const accessToken = await Auth0YofiTelemetryHelper._withTimeout(
        this._getAccessToken(),
        timeoutMs,
        () => console.warn(`Yofi: timed out after ${timeoutMs}ms; proceeding without Yofi cookie`),
      );
      if (!accessToken) return; // timed out or failed → do nothing
      Auth0CookieHelper.setCookie(
        yofiAccessTokenCookieName,
        accessToken,
        {
          expiry: Auth0CookieHelper.getExpirationFromDays(1),
          domain: window.location.home, // or location.hostname / .myapp.com depending on your needs
        },
      );
    } catch (e) {
      console.warn('Yofi: non-fatal error setting cookie', e);
    }
  };

  /**
   * Get the access token off the Yofi Session object
   * @returns {string|undefined}
   */
  static _getAccessToken = async () => {
    console.log('Auth0YofiTelemetryHelper._getAccessToken start');
    if (!window.YofiTelemetry) {
      console.error('Yofi Telemetry: Telemetry service not initialized');
      return;
    }

    try {
      const accessToken = await window.YofiTelemetry.getAppAccessToken();
      console.log('Auth0YofiTelemetryHelper._getAccessToken =>', accessToken);
      return accessToken;
    } catch (error) {
      console.error('Yofi Telemetry: Error getting access token', error);
    }
  };

  // generalized function wrapper to add timeout to any promise
  // We cannot control how yofi SDK behaves, so we need to make sure our login flow is not blocked by it
  static _withTimeout = (promise, ms, onTimeout) => {
    let settled = false;

    const timeoutP = new Promise((resolve) => {
      const id = setTimeout(() => {
        if (!settled && typeof onTimeout === 'function') onTimeout();
        resolve(undefined); // signal timeout
      }, ms);
      Promise.resolve(promise).finally(() => clearTimeout(id));
    });

    return Promise.race([
      Promise.resolve(promise)
        .then((v) => { settled = true; return v; })
        .catch(() => { settled = true; return undefined; }),
      timeoutP,
    ]);
  };
}

export default Auth0YofiTelemetryHelper;
